import { useEffect, useRef, useState } from 'react';
import { MOTION } from '@constants/motion';
import { num } from '@lib/format';
import { easeOutCubic } from '@lib/utils/math';
import { prefersReducedMotion } from '@lib/utils/motion';

/**
 * A number that rolls up/down to its new value (instant under reduced motion). `from` makes the first render
 * count from there (an entrance counting up from 0); later changes roll from the number shown.
 */
export function CountUp({ value, format = num, ms = MOTION.countUpMs, from: start0 }: { value: number; format?: (n: number) => string; ms?: number; from?: number }) {
  const first = start0 !== undefined && !prefersReducedMotion() ? start0 : value;
  const [shown, setShown] = useState(first);
  const from = useRef(first);
  useEffect(() => {
    const start = from.current;
    if (start === value || prefersReducedMotion()) {
      from.current = value;
      setShown(value);
      return;
    }
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / ms);
      const v = Math.round(start + (value - start) * easeOutCubic(k));
      from.current = v;
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return <span className="count">{format(shown)}</span>;
}
